export enum RecommendationAction {
  Accepted = 'ACCEPTED',
  Overridden = 'OVERRIDDEN',
  Rejected = 'REJECTED',
}

export enum RejectionReasonTag {
  AtRisk = 'AT_RISK',
  SpecialDeal = 'SPECIAL_DEAL',
  WrongData = 'WRONG_DATA',
}

export interface RejectionReason {
  note: string | null;
  tags: RejectionReasonTag[];
}

export interface RecommendationActionEntry {
  action: RecommendationAction;
  overrideGrossProfitInPercentage?: number;
  overridePriceInMicros?: number;
  recommendationUUID: string;
  rejectionReason?: RejectionReason;
  supplierUUID: string;
  /**
   * Signature of the decision as last persisted by the auto-publish status
   * sync. A signature that no longer matches the entry means it was edited
   * since, so the entry is unsaved again.
   */
  syncedSignature?: string;
}

export type RecommendationActionsSnapshot = Record<
  string,
  RecommendationActionEntry
>;

/**
 * All unpublished edit state for a single pricing task type: the edits, the
 * undo stack, and the rejection-reason history. Kept per type so navigating
 * away and back restores everything intact.
 */
export interface RecommendationActionsSlice {
  actions: RecommendationActionsSnapshot;
  actionSnapshotStack: RecommendationActionsSnapshot[];
  rejectedReasonHistoryMap: Record<string, RejectionReason>;
}

export interface RecommendationActionsStore {
  /**
   * Writes an explicit Accepted entry. For auto-publish rows this is the
   * inverse of `rejectRecommendation` — clearing the entry instead would let a
   * row rejected on the backend fall back to Rejected.
   */
  acceptRecommendation: (
    uuid: string,
    restaurantUUID: string,
    variantPackUUID: string,
    supplierUUID: string,
  ) => void;
  /** Active task type's edits (mirror of the active slice). */
  actions: RecommendationActionsSnapshot;
  /** Active task type's undo stack (mirror of the active slice). */
  actionSnapshotStack: RecommendationActionsSnapshot[];
  /**
   * Bumped whenever actions are wiped programmatically rather than by the user
   * (publish, stale prune, task switch). Consumers that diff against the
   * backend use it to drop their baseline instead of reading the wipe as a
   * deliberate per-row reset.
   */
  actionsResetEpoch: number;
  activeTaskTypeId: string | null;
  clearAllActions: () => void;
  clearOverride: (uuid: string) => void;
  cycleAction: (
    uuid: string,
    restaurantUUID: string,
    variantPackUUID: string,
    supplierUUID: string,
  ) => void;
  /**
   * Records that the given decisions are now stored on the backend, so progress
   * displays stop counting them as unpublished. Entries edited since they were
   * sent are left untouched.
   */
  markActionsSynced: (entries: RecommendationActionEntry[]) => void;
  /**
   * Drops active-type actions whose recommendation UUID is no longer present.
   * Called on detail load to purge edits left over from a previous run of the
   * same task type.
   */
  pruneStaleRecommendations: (validRecommendationUUIDs: string[]) => void;
  /**
   * Drops stashed slices for task types that no longer exist. Called on list
   * load to garbage-collect the persisted store.
   */
  pruneStaleTaskTypes: (validTaskTypeIds: string[]) => void;
  /** Active task type's reason history (mirror of the active slice). */
  rejectedReasonHistoryMap: Record<string, RejectionReason>;
  /**
   * Auto-publish reject: writes a Rejected entry, restoring the row's previous
   * reason if it has one. Auto-publish rows have no "none" state, so the only
   * way back is `acceptRecommendation`.
   */
  rejectRecommendation: (
    uuid: string,
    restaurantUUID: string,
    variantPackUUID: string,
    supplierUUID: string,
  ) => void;
  setOverridePrice: (
    uuid: string,
    priceInMicros: number,
    grossProfitInPercentage: number,
    supplierUUID: string,
  ) => void;
  setRejectionReason: (uuid: string, reason: RejectionReason) => void;
  /** Stashed slices for inactive task types, keyed by task_type_id. */
  slicesByType: Record<string, RecommendationActionsSlice>;
  /**
   * Makes the given task type active: stashes the current active slice and
   * hydrates the top-level mirror from the target type's slice (empty if
   * unseen), preserving each type's in-progress edits across navigation.
   */
  syncTaskType: (taskTypeId: string) => void;
  undo: () => void;
}
